import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import { getPlatformLogoSrc } from "../../components/Reports/Components/utils";
import { numberFormatter } from "../../helper/Reports/common";
import { useAmazonTargetingInsights } from "./dashboardHooks";
import SectionInfoIcon from "./SectionInfoIcon";

const userPlatform =
  JSON.parse(localStorage.getItem("user_platform")) || "amazon";

const MATCH_TYPE_CHIP_SX = {
  PHRASE: { bgcolor: "#D6EAF8", color: "#1A5276" },
  BROAD: { bgcolor: "#FDEBD0", color: "#B9770E" },
  EXACT: { bgcolor: "#D5F5E3", color: "#196F3D" },
};

const DEFAULT_COLUMNS = [
  { key: "targeting", title: "Targeting", isDimension: true, align: "left" },
  { key: "match_type", title: "Match Type", isDimension: true, align: "left" },
  {
    key: "spends",
    title: "Spends",
    isCurrencyFormat: true,
    maximumFractionDigits: 0,
  },
  { key: "clicks", title: "Clicks", maximumFractionDigits: 0 },
  { key: "orders", title: "Orders", maximumFractionDigits: 0 },
  {
    key: "revenue",
    title: "Revenue",
    isCurrencyFormat: true,
    maximumFractionDigits: 0,
  },
  { key: "roas", title: "ROAS", maximumFractionDigits: 2 },
];

const FUNNEL_COLUMNS = [
  { key: "targeting", title: "Targeting", isDimension: true, align: "left" },
  { key: "match_type", title: "Match Type", isDimension: true, align: "left" },
  {
    key: "spends",
    title: "Spends",
    isCurrencyFormat: true,
    maximumFractionDigits: 0,
  },
  { key: "orders", title: "Orders", maximumFractionDigits: 0 },
  { key: "roas", title: "ROAS", maximumFractionDigits: 2 },
  { key: "cvr", title: "CVR", maximumFractionDigits: 2 },
  {
    key: "cpc",
    title: "CPC",
    isCurrencyFormat: true,
    maximumFractionDigits: 2,
  },
];

const HEADER_CELL_SX = {
  borderBottom: "1px solid #E0E0E0",
  padding: "8px 12px",
  fontSize: "12px",
  color: "text.secondary",
  fontWeight: 600,
  bgcolor: "rgb(234, 230, 253)",
};

const BODY_CELL_SX = {
  padding: "12px",
  fontSize: "12px",
};

function MatchTypeChip({ matchType }) {
  if (!matchType) {
    return (
      <Typography variant="body2" sx={{ fontSize: "12px" }}>
        -
      </Typography>
    );
  }

  const label = String(matchType).toUpperCase();
  const colors = MATCH_TYPE_CHIP_SX[label] || {
    bgcolor: "#F0F0F0",
    color: "#555",
  };

  return (
    <Tooltip title={label} placement="top" arrow>
      <Chip
        label={label}
        size="small"
        sx={{
          height: 22,
          maxWidth: "100%",
          fontSize: "11px",
          fontWeight: 600,
          borderRadius: "12px",
          "& .MuiChip-label": {
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          ...colors,
        }}
      />
    </Tooltip>
  );
}

function MetricHeader({ title }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        flexDirection: "column",
        justifyContent: "flex-end",
        gap: 0.5,
      }}
    >
      <img
        src={getPlatformLogoSrc(userPlatform)}
        alt={userPlatform}
        style={{ width: "14px", height: "14px" }}
      />
      <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>{title}</Typography>
    </Box>
  );
}

function formatMetric(column, value) {
  if (value === null || value === undefined || value === "") return "-";
  return numberFormatter(
    value,
    !!column.isCurrencyFormat,
    column.format || "number",
    column.maximumFractionDigits
  );
}

/**
 * @param {object} props
 * @param {string[]} [props.selectedPortfolios]
 */
export default function TargetingInsights({
  selectedPortfolios = [],
  viewType: viewTypeProp,
  onViewTypeChange,
  performerType: performerTypeProp,
  onPerformerTypeChange,
} = {}) {
  const [internalViewType, setInternalViewType] = useState("Default");
  const [internalPerformerType, setInternalPerformerType] = useState("top");
  const viewType = viewTypeProp ?? internalViewType;
  const performerType = performerTypeProp ?? internalPerformerType;
  const setViewType = (next) => {
    if (onViewTypeChange) onViewTypeChange(next);
    else setInternalViewType(next);
  };
  const setPerformerType = (next) => {
    if (onPerformerTypeChange) onPerformerTypeChange(next);
    else setInternalPerformerType(next);
  };
  const { rows, loading } = useAmazonTargetingInsights(
    selectedPortfolios,
    performerType
  );

  const columns = viewType === "Funnel" ? FUNNEL_COLUMNS : DEFAULT_COLUMNS;

  const handleViewChange = (_, nextView) => {
    if (nextView !== null) setViewType(nextView);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2,
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: "16px" }}>
              Targeting Insights
            </Typography>
            <SectionInfoIcon
              lines={[
                "Grouped by targeting and match type for the selected dates and portfolios.",
                "Only enabled targeting, campaigns, and ad groups are included.",
                "Top 10: highest Spends, then highest ROAS. Bottom 10: lowest Spends, then lowest ROAS.",
                "Spends, Clicks, Orders, and Revenue are totals.",
                "ROAS = Revenue / Spends",
                "CVR = (Orders / Clicks) × 100",
                "CPC = Spends / Clicks",
              ]}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Monitor performance insights to spot growth opportunities and
            optimize results.
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={viewType}
          exclusive
          onChange={handleViewChange}
          aria-label="targeting view"
          sx={{
            flexShrink: 0,
            "& .MuiToggleButton-root": {
              px: 2,
              py: 0.65,
              textTransform: "none",
              fontWeight: 600,
              border: "1px solid",
              borderColor: "divider",
              "&.Mui-selected": {
                bgcolor: "primary.main",
                color: "white",
                "&:hover": {
                  bgcolor: "primary.dark",
                },
              },
            },
          }}
        >
          <ToggleButton size="small" value="Default">
            Default
          </ToggleButton>
          <ToggleButton size="small" value="Funnel">
            Funnel
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<TrendingUpRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={() => setPerformerType("top")}
          sx={{
            textTransform: "none",
            borderRadius: "20px",
            fontWeight: 600,
            px: 1.5,
            ...(performerType === "top"
              ? {
                  borderColor: "primary.main",
                  color: "primary.main",
                  bgcolor: "rgba(97, 72, 246, 0.08)",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "rgba(97, 72, 246, 0.12)",
                  },
                }
              : {
                  borderColor: "#E0E0E0",
                  color: "text.secondary",
                  bgcolor: "#fff",
                }),
          }}
        >
          Top 10 Performers
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<TrendingDownRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={() => setPerformerType("bottom")}
          sx={{
            textTransform: "none",
            borderRadius: "20px",
            fontWeight: 600,
            px: 1.5,
            ...(performerType === "bottom"
              ? {
                  borderColor: "primary.main",
                  color: "primary.main",
                  bgcolor: "rgba(97, 72, 246, 0.08)",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "rgba(97, 72, 246, 0.12)",
                  },
                }
              : {
                  borderColor: "#E0E0E0",
                  color: "text.secondary",
                  bgcolor: "#fff",
                }),
          }}
        >
          Bottom 10 Performers
        </Button>
      </Box>

      <Paper
        sx={{
          borderRadius: 2,
          boxShadow: "none",
          border: "1px solid #E0E0E0",
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.align || "right"}
                    sx={HEADER_CELL_SX}
                  >
                    {column.isDimension ? (
                      <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                        {column.title}
                      </Typography>
                    ) : (
                      <MetricHeader title={column.title} />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? [...Array(10)].map((_, rowIndex) => (
                    <TableRow key={`skeleton-${rowIndex}`}>
                      {columns.map((column) => (
                        <TableCell
                          key={column.key}
                          align={column.align || "right"}
                          sx={{
                            ...BODY_CELL_SX,
                            borderBottom:
                              rowIndex === 9 ? "none" : "1px solid #E0E0E0",
                          }}
                        >
                          <Skeleton
                            width={column.isDimension ? "80%" : "60px"}
                            height={20}
                            sx={{
                              ml: column.isDimension ? 0 : "auto",
                            }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : rows.length === 0
                  ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        align="center"
                        sx={{ ...BODY_CELL_SX, py: 4, borderBottom: "none" }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No targeting data for the selected period
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                  : rows.map((row, rowIndex) => (
                      <TableRow key={`${row?.targeting}-${row?.match_type}-${rowIndex}`}>
                        {columns.map((column) => (
                          <TableCell
                            key={column.key}
                            align={column.align || "right"}
                            sx={{
                              ...BODY_CELL_SX,
                              ...(column.key === "match_type"
                                ? { overflow: "hidden", maxWidth: 0 }
                                : {}),
                              borderBottom:
                                rowIndex === rows.length - 1
                                  ? "none"
                                  : "1px solid #E0E0E0",
                            }}
                          >
                            {column.key === "targeting" ? (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "12px",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  lineHeight: 1.4,
                                }}
                              >
                                {row?.targeting || "-"}
                              </Typography>
                            ) : column.key === "match_type" ? (
                              <MatchTypeChip matchType={row?.match_type} />
                            ) : (
                              formatMetric(column, row?.[column.key])
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
